// Import tslib first to ensure helpers are available
import * as tslib from 'tslib'
// Make tslib available globally for pre-compiled modules
;

import { Suspense, lazy } from "react"
import ReactDOM from "react-dom/client"
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"

import Main from "./layouts/Main"
import AuthProvider from "./layouts/AuthProvider"
import { Spinner } from "./components/common/Spinner"
import "./index.css"
import "./styles/reports-print.css"
import "./styles/ant-podops-normalization.css"

// Import ErrorBoundary
import ErrorBoundary from "./components/common/ErrorBoundary"
import { DialogProvider } from "./contexts/DialogContext"

(window as any).__assign = tslib.__assign

// Create a React Query client with optimized caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,     // Data considered fresh for 2 minutes
      gcTime: 5 * 60 * 1000,         // Keep in cache for 5 minutes (renamed from cacheTime in react-query v5)
      retry: 3,                       // Retry failed queries 3 times
      retryDelay: (attemptIndex) =>
        Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      refetchOnWindowFocus: false,   // Don't refetch on tab focus
      refetchOnMount: true,           // Refetch when component mounts if stale
    },
    mutations: {
      retry: 1,                       // Retry mutations once on failure
    },
  },
})

// Lazy load routes for better code splitting
const RootRoute = lazy(() => import("./routes/Root"))
const LogoutRoute = lazy(() => import("./routes/Logout"))
const CalibrationRoute = lazy(() => import("./routes/Calibration"))

const root = ReactDOM.createRoot(document.getElementById("root") as any)

// Loading fallback component
const LoadingFallback = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
    <Spinner size="large" />
  </div>
)

const AppRoutes = () => {
  return (
    <Routes>
      {/* Root route - redirects to /calibration when signed in, otherwise to Entra */}
      <Route index element={<Suspense fallback={<LoadingFallback />}><RootRoute /></Suspense>} />

      {/* Authenticated routes wrapped in Main layout.
          MSAL's redirectUri is the origin, so the sign-in redirect lands on "/"
          and is handled by AuthProvider — no dedicated callback route needed. */}
      <Route path="/" element={<Main />}>
        <Route path="calibration" element={
          <ErrorBoundary componentName="Calibration">
            <Suspense fallback={<LoadingFallback />}><CalibrationRoute /></Suspense>
          </ErrorBoundary>
        } />
        <Route path="logout" element={<Suspense fallback={<LoadingFallback />}><LogoutRoute /></Suspense>} />
      </Route>

      {/* Catch-all redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}

root.render(
  <QueryClientProvider client={queryClient}>
    <DialogProvider>
      <BrowserRouter
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </DialogProvider>
  </QueryClientProvider>,
)
